import { Attribute, Relationship, Resource } from 'json-api';
import { Translatable } from 'src/app/translation/translatable';
import { AtomResource } from '../classes/atom-resource';
import { AcquisitionAccounts } from './acquisition-account.resource';
import { AcquisitionSources } from './acquisition-source.resource';
import { BusinessUnit } from './business-unit.resource';
import { Firm } from './firm.resource';
import { Product } from './product.resource';
import { TrafficManager } from './traffic-manager.resource';
import { NumberFilter, StringFilter } from 'src/app/filter/filter';
import {
  AcquisitionAccountConfig,
  AcquisitionSourceConfig,
  FirmConfig,
  TrafficManagerConfig,
} from 'src/app/shared/tools/search/search-config';
import { AtomService } from '../atom.service';
import { ResourceConfigOne } from 'src/app/resources/resources-interfaces/resource-config';

@Resource({
  type: 'acquisitionmappings',
})
export class AcquisitionMapping extends AtomResource {
  @Attribute() name?: Translatable;
  @Attribute() validity?: string;
  @Attribute() date?: string;
  @Attribute({ key: 'validity_tz' }) validityTz?: string;
  @Attribute() data?: Record<string, unknown>;

  @Relationship() billingfirm?: Firm;
  @Relationship() trafficmanager?: TrafficManager;
  @Relationship() acquisitionaccount?: AcquisitionAccounts;
  @Relationship() acquisitionsource?: AcquisitionSources;
}

export class AcquisitionMappingConfig {
  tables = {
    includes: [
      'billingfirm.headquarterscountry',
      'acquisitionsource',
      'acquisitionaccount',
      'trafficmanager',
    ],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main', translatable: false }],
        header: 'ID',
        translatable: false,
        displayed: true,
      },
      {
        columnDef: 'acquisitionsource',
        displayedDatas: [
          {
            path: ['acquisitionsource.name'],
            class: 'main',
            translatable: false,
          },
        ],
        header: 'Source',
        translatable: false,
        displayed: true,
        link: {
          resource: 'acquisitionsource',
          filter: 'acquisitionsource.id',
          icon: 'water_drop',
        },
      },
      {
        columnDef: 'acquisitionaccount',
        displayedDatas: [
          {
            path: ['acquisitionaccount.name'],
            class: 'main',
            translatable: false,
          },
          {
            path: ['acquisitionaccount.account'],
            class: 'mat-body-1',
            translatable: false,
          },
        ],
        header: 'Account',
        translatable: false,
        displayed: true,
        link: {
          resource: 'acquisitionaccounts',
          filter: 'acquisitionaccount.id',
          icon: 'fingerprint',
        },
      },
      {
        columnDef: 'validity',
        header: 'Validity',
        displayed: true,
      },
      {
        columnDef: 'billingfirm',
        displayedDatas: [
          { path: ['billingfirm.name'], class: 'main', translatable: true },
          {
            path: ['billingfirm.headquarterscountry.id'],
            class: 'mat-body-1',
            translatable: false,
          },
        ],
        header: 'Firm',
        translatable: true,
        displayed: true,
        options: {
          flag: 'billingfirm.headquarterscountry.id',
        },
        link: {
          resource: 'firms',
          filter: 'firm.id',
          icon: 'business',
        },
      },
      {
        columnDef: 'trafficmanager',
        displayedDatas: [
          {
            path: ['trafficmanager.name'],
            class: 'main',
            translatable: false,
          },
        ],
        header: 'Traffic Manager',
        translatable: false,
        displayed: true,
        link: {
          resource: ['trafficmanagers'],
          filter: 'trafficmanager.id',
          icon: 'person',
        },
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        translatable: false,
        displayed: true,
      },
    ],
  };
  detail: ResourceConfigOne = {
    header: {
      icon: 'merge',
      title: [
        {
          path: ['acquisitionaccount.account'],
          translatable: false,
        },
      ],
      id: true,
      validity: true,
    },
    // elements: [
    //   {
    //     list: [
    //       {
    //         type: 'validity',
    //       },
    //     ],
    //   },
    // ],
    tabs: [
      {
        title: 'Mapping',
        elements: [
          {
            showEmpty: true,
            list: [
              {
                icon: 'business',
                title: 'Billing Firm',
                APIKey: 'billingfirm',
                value: {
                  path: ['billingfirm.name'],
                  translatable: true,
                },
                link: {
                  resource: 'firms',
                  filter: 'billingfirm.id',
                },
                options: {
                  flag: 'billingfirm.headquarterscountry.id',
                },
              },
              {
                icon: 'water_drop',
                title: 'Source',
                APIKey: 'acquisitionsource',
                value: {
                  path: ['acquisitionsource.name'],
                  translatable: false,
                },
                link: {
                  resource: 'acquisitionsources',
                  filter: 'acquisitionsource.id',
                },
              },
              {
                icon: 'fingerprint',
                title: 'Account',
                APIKey: 'acquisitionaccount',
                value: {
                  path: ['acquisitionaccount.account'],
                  translatable: false,
                },
                link: {
                  resource: 'acquisitionaccounts',
                  filter: 'acquisitionaccount.id',
                },
              },
              {
                icon: 'person',
                title: 'Traffic Manager',
                APIKey: 'trafficmanager',
                value: {
                  path: ['trafficmanager.name'],
                  translatable: false,
                },
                link: {
                  resource: 'trafficmanagers',
                  filter: 'trafficmanager.id',
                  icon: 'person',
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Data',
        elements: [
          {
            showEmpty: true,
            list: [
              {
                type: 'data',
                title: 'Datas',
                APIKey: 'data',
              },
            ],
          },
        ],
      },
    ],
  };
  wizard = {
    tabs: [
      {
        title: 'Mapping',
        formGroup: 'mapping',
        options: {
          type: 'sequential',
        },
        list: [
          {
            type: 'resource',
            title: 'Source',
            formControl: 'acquisitionsource',
            search: new AcquisitionSourceConfig(),
            required: true,

            activatingSequence: [
              {
                group: 'mapping',
                control: 'acquisitionaccount',
                filters: {
                  filter: 'equal.acquisitionsource.id',
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Account',
            formControl: 'acquisitionaccount',
            search: new AcquisitionAccountConfig(),
            required: true,
          },
          {
            type: 'resource',
            title: 'Billing Firm',
            formControl: 'billingfirm',
            search: new FirmConfig(this.atom),
            required: true,
          },
          {
            type: 'resource',
            title: 'Traffic Maanger',
            formControl: 'trafficmanager',
            search: new TrafficManagerConfig(),
            required: true,
          },
        ],
      },
      {
        title: 'Validity & Data',
        formGroup: 'validity',
        list: [
          {
            type: 'validity',
            title: 'Validity',
            formControl: 'validity',
            required: true,
          },
          {
            type: 'data',
            title: 'Data',
            formControl: 'data',
            required: false,
          },
        ],
      },
    ],
  };
  resource = AcquisitionMapping;
  resourceName = 'Mapping';
  resourceKey = 'acquisitionmappings';
  includes = [
    'billingfirm.headquarterscountry',
    'acquisitionsource.acquisitionaccounts',
    'acquisitionaccount',
    'trafficmanager',
  ];
  filters = [
    new NumberFilter('id', 'ID', true),
    new StringFilter('acquisitionsource.name', 'Source Name'),
    new NumberFilter('acquisitionsource.id', 'Source ID'),
    new StringFilter('acquisitionaccount.name', 'Account Name'),
    new NumberFilter('acquisitionaccount.id', 'Account ID'),
    new StringFilter('billingfirm.name', 'Billing Firm Name'),
    new NumberFilter('billingfirm.id', 'Billing Firm ID'),
    new NumberFilter('trafficmanager.id', 'Traffic Manager ID'),
    new StringFilter('trafficmanager.name', 'Traffic Manager Name'),
    new StringFilter('validity', 'Validity'),
  ];
  constructor(private atom: AtomService) {}
}
